import Transport from 'winston-transport';
import { bizmatesApi } from '@/utils/bizmatesApi';
import { jwtHelperMyStage } from '@/utils/jwt';
import store from '@/store/index';
import { store as localStorage } from '@/utils/common';
import qs from 'qs';

/** Class representing the Bizmates Winston Transport
 * reference: https://github.com/winstonjs/winston#adding-custom-transports
*/
class BizTransport extends Transport {
  /**
   * Create the transport
   * @param {Object} options - The options for configuring the transport.
   */
  constructor(options) {
    super(options);

    options = options || {};
    this.lessonToken = options.lessonToken || null;
    this.jwtKeyValue = options.jwtKeyValue || null;

    /**
     * RFC 5424
     * refer: https://www.rfc-editor.org/rfc/rfc5424.txt
     *  0       Emergency: system is unusable
     *  1       Alert: action must be taken immediately
     *  2       Critical: critical conditions
     *  3       Error: error conditions
     *  4       Warning: warning conditions
     *  5       Notice: normal but significant condition
     *  6       Informational: informational messages
     *  7       Debug: debug-level messages
     */
    this.postLevels = {
      error: 3,
      warn: 4,
      notice: 5,
      info: 6,
      debug: 7
    }

    /**
     *  Default Result Code
     */
    this.resultCode = {
      success: 0,
      error: -1
    }

    /**
     * Limit to number of logs to temporarily keep in store
     */
    this.logLimit = 20;
  }


  /**
   * Log a message to Bizmates.jp log api.
   *
   * @param {Object} info - An object containing the `message`, `level`, `Symbol(level)`, `Symbol(message)`.
   * @param {function} callback - Winston's callback to itself.
   */
  log(info, callback) {
    if (jwtHelperMyStage.isGracePeriodEnd(this.lessonToken) && !this.jwtKeyValue) {
      return false;
    }

    const config = {
      headers: {
        Authorization: 'Bearer ' + this.lessonToken
      },
      isTrial: this.jwtKeyValue
    };

    let infoCopy = JSON.parse(JSON.stringify(info)), postInfo = {};

    if (infoCopy.hasOwnProperty('message')) {
      postInfo.msg = infoCopy.message;
      delete infoCopy.message;
    }

    if (infoCopy.hasOwnProperty('result')) {
      postInfo.res = infoCopy.result;
      delete infoCopy.result;
    } else {
      postInfo.res = info.level === 'error' ? this.resultCode.error : this.resultCode.success;
    }

    if (infoCopy.hasOwnProperty('level')) {
      postInfo.sev = this.postLevels[info.level];
      delete infoCopy.level;
    }

    if (infoCopy.hasOwnProperty('operation')) {
      postInfo.op = infoCopy.operation;
      delete infoCopy.operation;
      this.logLimit = (postInfo.op == 'send-batch-log' ? 1 : 20);
    }

    if (Object.keys(infoCopy).length) {
      postInfo.rsn = infoCopy; // assign all other JSON fields (if any) to the `rsn` field of POST payload JSON
    }

    // DEBUG: (please don't delete these lines, they might help someone later debugging this code)
    // console.log('Original info:', info);
    // console.log('POST payload to Bizmates.jp API:', postInfo);

    /* 
    * Send logs in bulk (20 per batch) with log level "info"
    * This is to minimize server load from sending large amount of logs
    * Send logs immediately with high priority tag
    */
    let sendLog = true;
    const isHighPriorityLog = postInfo.rsn?.priorityLog;
    if (info.level == 'info' && !isHighPriorityLog) {
      const lessonId = store.state.lessonId;

      let currentLogData = localStorage.getLog(lessonId);
      if (!Array.isArray(currentLogData)) {
        currentLogData = [];
      }
      
      localStorage.addLog(lessonId, [postInfo, ...currentLogData]);
      const pendingLogs = localStorage.getLog(lessonId);

      if (pendingLogs.length >= this.logLimit) {
        postInfo = {};
        postInfo = pendingLogs;
        localStorage.emptyLog(lessonId);
      } else {
        sendLog = false;
        callback(null, null);
        this.emit('logged', info);
      }
    }

    if (store.state.isUserAuth && !store.state.isLessonEnded && sendLog) {
      bizmatesApi.postLog(
        qs.stringify({ payload: JSON.stringify(postInfo), ...this.jwtKeyValue }),
        config,
        result => {
          callback(null, result);
          this.emit('logged', info);
        }, error => {
          if (error.response.status === 401) {
            store.commit('setIsUserAuth', false);
          }
          callback(error);
          this.emit('error', error);
       });
    }
  }
}

export default BizTransport;
